import { adminFetch } from "./base";
import type { Product } from "@/lib/validation/product";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProductListResponse {
  readonly products: readonly Product[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface ProductFilters {
  readonly search?: string | null;
  readonly brand?: string | null;
  readonly category?: string | null;
  readonly status?: string | null;
  readonly stock?: string | null;
  readonly page?: number;
  readonly limit?: number;
  readonly sort_by?: string;
  readonly sort_dir?: "asc" | "desc";
}

export interface AlertsCountResponse {
  readonly out_of_stock: number;
  readonly low_stock: number;
  readonly total_alerts: number;
  readonly last_computed_at: string;
}

// ---------------------------------------------------------------------------
// API module
// ---------------------------------------------------------------------------

function buildQueryString(filters: ProductFilters): string {
  const params = new URLSearchParams();

  if (filters.page) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.search) params.set("search", filters.search);
  if (filters.brand) params.set("brand", filters.brand);
  if (filters.category) params.set("category", filters.category);
  if (filters.status) params.set("status", filters.status);
  if (filters.stock) params.set("stock", filters.stock);
  if (filters.sort_by) params.set("sort_by", filters.sort_by);
  if (filters.sort_dir) params.set("sort_dir", filters.sort_dir);

  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildFilterSuffix(filters: ProductFilters): string {
  const qs = buildQueryString(filters);
  return qs ? `&${qs.slice(1)}` : "";
}

export const productsApi = {
  /** Fetch paginated product list with optional filters */
  list: (filters: ProductFilters = {}) =>
    adminFetch<ProductListResponse>(
      `/api/admin/products${buildQueryString(filters)}`,
    ),

  /** Fetch a single product by ID */
  get: (id: string) => adminFetch<Product>(`/api/admin/products/${id}`),

  /** Create a new product */
  create: (data: Record<string, unknown>) =>
    adminFetch<Product>("/api/admin/products", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** Update an existing product */
  update: (id: string, data: Record<string, unknown>) =>
    adminFetch<Product>(`/api/admin/products/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** Soft-delete (deactivate) a product */
  deactivate: (id: string) =>
    adminFetch<{ success: boolean; message: string }>(
      `/api/admin/products/${id}`,
      { method: "DELETE" },
    ),

  /** Bulk CSV import */
  importCSV: (rows: readonly Record<string, unknown>[]) =>
    adminFetch<{ imported: number; errors: readonly string[] }>(
      "/api/admin/products/import",
      {
        method: "POST",
        body: JSON.stringify({ rows }),
      },
    ),

  /** Download a CSV export of products matching the given filters. */
  exportCsv: (filters: ProductFilters = {}): Promise<Blob> =>
    adminFetch<Blob>(
      `/api/admin/products/export?format=csv${buildFilterSuffix(filters)}`,
      { responseType: "blob" },
    ),

  /** Fetch low-stock / out-of-stock alert counts for dashboard widgets. */
  alertsCount: () =>
    adminFetch<AlertsCountResponse>("/api/admin/products/alerts/count"),
} as const;
